import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCLP, CATEGORIES } from "../lib/api";
import { LogOut, Package, ShoppingBag, BarChart3, Eye, Trash2, Plus, X, Edit, Image as ImageIcon, Loader2, Settings as SettingsIcon, Save, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "orders", label: "Pedidos", icon: ShoppingBag },
  { key: "products", label: "Productos", icon: Package },
  { key: "settings", label: "Configuración", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);

  const logout = () => {
    localStorage.removeItem("torvi_admin_token");
    navigate("/admin/login");
  };

  const loadAll = async () => {
    try {
      const [s, o, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/orders"),
        api.get("/products"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setProducts(p.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sesión expirada");
        navigate("/admin/login");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("torvi_admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center font-bold text-sm">T</div>
            <div className="font-heading font-black text-lg">Admin · Torvi</div>
          </div>
          <button onClick={logout} className="text-sm font-bold inline-flex items-center gap-2 text-slate-600 hover:text-black" data-testid="admin-logout-btn">
            <LogOut size={15} /> Salir
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {TABS.map((t) => {
            const Ico = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-4 py-2.5 rounded-full font-bold text-sm inline-flex items-center gap-2 whitespace-nowrap transition ${
                  tab === t.key ? "bg-black text-white" : "bg-white border border-slate-200 hover:bg-slate-100"
                }`}
              >
                <Ico size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "dashboard" && <DashboardTab stats={stats} />}
        {tab === "orders" && <OrdersTab orders={orders} reload={loadAll} />}
        {tab === "products" && <ProductsTab products={products} reload={loadAll} />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

const SettingsTab = () => {
  const [paymentLink, setPaymentLink] = useState("");
  const [bankInfo, setBankInfo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => { setPaymentLink(r.data.payment_link || ""); setBankInfo(r.data.bank_info || ""); })
      .catch(() => toast.error("Error al cargar configuración"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { payment_link: paymentLink, bank_info: bankInfo });
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">Cargando...</div>;

  return (
    <div className="max-w-2xl" data-testid="settings-content">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1.5 inline-flex items-center gap-2">
            <LinkIcon size={13} /> Link de pago externo
          </div>
          <input
            type="url"
            value={paymentLink}
            onChange={(e) => setPaymentLink(e.target.value)}
            placeholder="https://www.mercadopago.cl/..."
            data-testid="settings-payment-link"
            className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] outline-none"
          />
          <p className="text-xs text-slate-500 mt-1.5">Este link se abrirá cuando un cliente presione "Pagar" en su carrito.</p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1.5">Datos de transferencia (texto libre)</div>
          <textarea
            value={bankInfo}
            onChange={(e) => setBankInfo(e.target.value)}
            rows={4}
            data-testid="settings-bank-info"
            className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] outline-none"
          />
          <p className="text-xs text-slate-500 mt-1.5">Información que verán los clientes en el link / WhatsApp si lo compartes.</p>
        </div>

        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="save-settings-btn">
          {saving ? <><Loader2 size={16} className="animate-spin" /> Guardando...</> : <><Save size={16} /> Guardar cambios</>}
        </button>
      </div>
    </div>
  );
};

const DashboardTab = ({ stats }) => {
  if (!stats) return <div className="text-slate-500">Cargando estadísticas...</div>;
  const cards = [
    { label: "Ventas totales", value: fmtCLP(stats.total_sales), color: "bg-[#0EA5E9]" },
    { label: "Pedidos totales", value: stats.total_orders, color: "bg-black" },
    { label: "Pendientes", value: stats.pending, color: "bg-amber-500" },
    { label: "Pagados", value: stats.paid, color: "bg-emerald-500" },
    { label: "Entregados", value: stats.delivered, color: "bg-purple-500" },
    { label: "Productos", value: stats.products_count, color: "bg-slate-700" },
  ];
  return (
    <div data-testid="dashboard-content">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className={`w-2 h-10 ${c.color} rounded-full mb-3`} />
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{c.label}</div>
            <div className="font-heading font-black text-3xl mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      {stats.sales_series?.length > 0 && (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4">Ventas últimos días</div>
          <div className="flex items-end gap-2 h-40">
            {stats.sales_series.map((d) => {
              const max = Math.max(...stats.sales_series.map((x) => x.total)) || 1;
              const h = (d.total / max) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-[#0EA5E9] rounded-t" style={{ height: `${h}%` }} />
                  <div className="text-[10px] text-slate-500">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const STATUS_OPTIONS = ["pendiente", "pagado", "en_preparacion", "enviado", "entregado", "cancelado"];
const STATUS_COLORS = {
  pendiente: "bg-amber-100 text-amber-700",
  pagado: "bg-emerald-100 text-emerald-700",
  en_preparacion: "bg-sky-100 text-sky-700",
  enviado: "bg-indigo-100 text-indigo-700",
  entregado: "bg-purple-100 text-purple-700",
  cancelado: "bg-red-100 text-red-700",
};

const OrdersTab = ({ orders, reload }) => {
  const [view, setView] = useState(null);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Estado actualizado");
      reload();
      if (view) setView({ ...view, status });
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const remove = async (id, status) => {
    if (status === "pendiente") {
      toast.error("No se pueden eliminar pedidos en estado pendiente. Cámbialo a otro estado primero.");
      return;
    }
    if (!window.confirm("¿Eliminar este pedido?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Pedido eliminado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const downloadImage = (dataUrl, filename) => {
    try {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename || "imagen.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("No se pudo descargar la imagen");
    }
  };

  return (
    <div data-testid="orders-content">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-3 font-bold">Pedido</th>
              <th className="px-4 py-3 font-bold">Cliente</th>
              <th className="px-4 py-3 font-bold">Total</th>
              <th className="px-4 py-3 font-bold">Estado</th>
              <th className="px-4 py-3 font-bold">Fecha</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-500">No hay pedidos aún.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 font-bold">{o.order_number}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{o.customer.nombre} {o.customer.apellido}</div>
                  <div className="text-xs text-slate-500">{o.customer.telefono}</div>
                </td>
                <td className="px-4 py-3 font-bold">{fmtCLP(o.total)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(o.created_at).toLocaleString("es-CL")}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setView(o)} className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-bold inline-flex items-center gap-1" data-testid={`view-order-${o.id}`}>
                    <Eye size={13} /> Ver
                  </button>
                  <button onClick={() => remove(o.id, o.status)} disabled={o.status === "pendiente"} className={`ml-1 px-2.5 py-1.5 rounded-full text-xs font-bold ${o.status === "pendiente" ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-red-50 hover:bg-red-100 text-red-600"}`} title={o.status === "pendiente" ? "Cambia el estado antes de eliminar" : "Eliminar pedido"} data-testid={`delete-order-${o.id}`}>
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-view-dialog">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-black text-2xl">Pedido {view.order_number}</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4 mt-2">
                <Section title="Cliente">
                  {view.user_id && <Row k="Cuenta" v="Cliente registrado ✓" />}
                  <Row k="Nombre" v={`${view.customer.nombre} ${view.customer.apellido}`} />
                  <Row k="RUT" v={view.customer.rut} />
                  <Row k="Teléfono" v={view.customer.telefono} />
                  <Row k="Correo" v={view.customer.correo} />
                  <Row k="Dirección" v={view.customer.direccion} />
                  {view.customer.observacion && <Row k="Observación" v={view.customer.observacion} />}
                </Section>
                <Section title="Pago">
                  <Row k="Subtotal" v={fmtCLP(view.subtotal)} />
                  <Row k="Total" v={fmtCLP(view.total)} />
                  <Row k="Regalo" v={view.has_gift ? "Sí 🎁" : "No"} />
                  <div className="mt-3">
                    <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1.5">Estado</label>
                    <select value={view.status} onChange={(e) => updateStatus(view.id, e.target.value)} data-testid="order-status-select" className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </Section>
              </div>
              <Section title="Productos">
                <div className="space-y-3">
                  {view.items.map((it, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-3 space-y-3">
                      <div className="flex gap-3 items-center">
                        <div className="w-14 h-14 rounded-lg bg-white overflow-hidden flex-shrink-0">
                          {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm">{it.name}</div>
                          {it.custom_text && <div className="text-xs text-slate-500">Texto: {it.custom_text}</div>}
                          {it.phone_model && <div className="text-xs text-slate-500">Modelo: {it.phone_model}</div>}
                        </div>
                        <div className="text-right">
                          <div className="text-xs">x{it.quantity}</div>
                          <div className="font-bold">{fmtCLP(it.price * it.quantity)}</div>
                        </div>
                      </div>
                      {it.custom_image && (
                        <div className="bg-white rounded-lg border border-amber-200 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-bold uppercase tracking-widest text-amber-700 flex items-center gap-1">
                              📸 Foto personalizada del cliente
                            </div>
                            <div className="flex gap-1.5">
                              <a
                                href={it.custom_image}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-[11px] font-bold inline-flex items-center gap-1"
                                data-testid={`open-custom-image-${i}`}
                              >
                                <Eye size={12} /> Abrir
                              </a>
                              <button
                                onClick={() => downloadImage(it.custom_image, `pedido-${view.order_number}-funda-${i + 1}.png`)}
                                className="px-2.5 py-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[11px] font-bold inline-flex items-center gap-1"
                                data-testid={`download-custom-image-${i}`}
                              >
                                ⬇ Descargar
                              </button>
                            </div>
                          </div>
                          <a href={it.custom_image} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={it.custom_image}
                              alt="Foto personalizada"
                              className="w-full max-h-80 object-contain rounded-lg bg-slate-50 cursor-zoom-in"
                              data-testid={`custom-image-${i}`}
                            />
                          </a>
                          <div className="text-[10px] text-slate-400 mt-1.5 text-center">
                            Click en la imagen para abrirla en tamaño real
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
              <Section title="Comprobante de pago">
                {view.receipt_image ? (
                  <div>
                    <div className="flex justify-end gap-1.5 mb-2">
                      <a href={view.receipt_image} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-[11px] font-bold inline-flex items-center gap-1">
                        <Eye size={12} /> Abrir
                      </a>
                      <button onClick={() => downloadImage(view.receipt_image, `pedido-${view.order_number}-comprobante.png`)} className="px-2.5 py-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[11px] font-bold inline-flex items-center gap-1">
                        ⬇ Descargar
                      </button>
                    </div>
                    <a href={view.receipt_image} target="_blank" rel="noreferrer">
                      <img src={view.receipt_image} alt="comprobante" className="max-h-96 w-full object-contain rounded-xl border border-slate-200 bg-slate-50 cursor-zoom-in" data-testid="order-receipt-img" />
                    </a>
                  </div>
                ) : (
                  <div className="text-slate-500 text-sm">Sin comprobante</div>
                )}
              </Section>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 mt-4">
    <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">{title}</div>
    <div className="space-y-1.5 text-sm">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{k}</span>
    <span className="font-bold text-black text-right">{v}</span>
  </div>
);

const ProductsTab = ({ products, reload }) => {
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Producto eliminado");
      reload();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div data-testid="products-content">
      <div className="flex justify-between items-center mb-4">
        <div className="text-slate-600 text-sm">{products.length} productos</div>
        <button onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="new-product-btn">
          <Plus size={16} /> Nuevo producto
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex" data-testid={`admin-product-${p.id}`}>
            <div className="w-28 h-28 bg-slate-100 flex-shrink-0">
              {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-3 flex-1 min-w-0">
              <div className="font-bold text-sm leading-tight line-clamp-2">{p.name}</div>
              <div className="font-heading font-black text-base mt-1">{fmtCLP(p.price)}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{p.category}</div>
              <div className="flex gap-1.5 mt-2">
                <button onClick={() => setEdit(p)} className="px-2 py-1 rounded-full bg-slate-100 text-xs font-bold inline-flex items-center gap-1" data-testid={`edit-product-${p.id}`}>
                  <Edit size={11} /> Editar
                </button>
                <button onClick={() => remove(p.id)} className="px-2 py-1 rounded-full bg-red-50 text-red-600 text-xs font-bold" data-testid={`delete-product-${p.id}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(edit || creating) && (
        <ProductFormDialog
          product={edit}
          onClose={() => { setEdit(null); setCreating(false); }}
          onSaved={() => { setEdit(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
};

const ProductFormDialog = ({ product, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || 0,
    category: product?.category || CATEGORIES[0].key,
    stock: product?.stock || 100,
    images: product?.images || [],
    is_customizable: product?.is_customizable || false,
  });
  const [imgUrl, setImgUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name || !form.price) {
      toast.error("Nombre y precio son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (product) {
        await api.put(`/products/${product.id}`, form);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", form);
        toast.success("Producto creado");
      }
      onSaved();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((p) => ({ ...p, images: [...p.images, ev.target.result] }));
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl">{product ? "Editar producto" : "Nuevo producto"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input label="Nombre" value={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} testId="form-name" />
          <Textarea label="Descripción" value={form.description} onChange={(v) => setForm((p) => ({ ...p, description: v }))} testId="form-description" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" label="Precio (CLP)" value={form.price} onChange={(v) => setForm((p) => ({ ...p, price: parseInt(v || "0") }))} testId="form-price" />
            <Input type="number" label="Stock" value={form.stock} onChange={(v) => setForm((p) => ({ ...p, stock: parseInt(v || "0") }))} testId="form-stock" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">Categoría</label>
            <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="form-category">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={form.is_customizable} onChange={(e) => setForm((p) => ({ ...p, is_customizable: e.target.checked }))} data-testid="form-customizable" />
            Es personalizable
          </label>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">Imágenes</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-100">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm((p) => ({ ...p, images: p.images.filter((_, j) => j !== i) }))} className="absolute top-0 right-0 bg-black/70 text-white w-5 h-5 flex items-center justify-center text-xs">×</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="URL de imagen (opcional)" className="flex-1 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm" />
              <button onClick={() => { if (imgUrl) { setForm((p) => ({ ...p, images: [...p.images, imgUrl] })); setImgUrl(""); } }} className="px-3 py-2 rounded-lg bg-slate-100 text-sm font-bold">+ URL</button>
            </div>
            <input type="file" accept="image/*" onChange={onPickImage} className="mt-2 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-full bg-slate-100 font-bold">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="save-product-btn">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Input = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] outline-none" />
  </div>
);
const Textarea = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1.5">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] outline-none" />
  </div>
);
